import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { OrgSubnav } from '../../components/shell/OrgSubnav';
import { TeamViews, TeamsTable, TeamPeek } from '../../components/organizations-ledger';
import { TeamFormDialog } from '../../components/organizations';
import { LedgerDialog, LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { TeamListParams, useTeamsPage, useTeamDetail, useDeleteTeam } from '../../hooks/useOrgQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import { Team } from '../../types/organization.types';
import { cn } from '../../lib/cn';

export const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  // Cross-nav entry point: CompanyPeek links here as ?company=<id>. Seed the
  // filter from the URL so the very first fetch is already scoped.
  const [company, setCompany] = useState<string | 'ALL'>(
    () => new URLSearchParams(location.search).get('company') ?? 'ALL',
  );
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Team | null>(null);
  const [deleting, setDeleting] = useState<Team | null>(null);
  const toast = useToast();

  // Absorb the ?company= param into filter state, then strip it so refresh,
  // back-navigation, or copied links don't replay a stale filter.
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('company');
    if (fromUrl == null) return;
    setCompany(fromUrl);
    setPage(1);
    navigate(location.pathname, { replace: true });
  }, [location, navigate]);

  const params: TeamListParams = useMemo(
    () => ({ search, company, activeOnly, page }),
    [search, company, activeOnly, page],
  );
  const { data, isPending, isError } = useTeamsPage(params);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;

  useEffect(() => {
    if (isError) toast.error('Failed to load teams');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Tailwind's lg breakpoint; the peek is a right rail on desktop and a bottom
  // sheet on mobile, and the sheet portals to <body>, so CSS can't gate it.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const selectedTeam = rows.find((team) => team.id === selectedId) ?? null;
  // A selected team can leave the current page (filter or page change); fetch
  // it by id so the peek survives.
  const peekFallback = useTeamDetail(peekOpen && selectedTeam == null ? selectedId : null);
  const peekTeam = selectedTeam ?? peekFallback.data ?? null;

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((team) => team.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  useLedgerListKeys({
    enabled: !formOpen && deleting == null,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const changeCompany = (value: string | 'ALL') => {
    setCompany(value);
    setPage(1);
  };
  const changeActiveOnly = (value: boolean) => {
    setActiveOnly(value);
    setPage(1);
  };
  const resetView = () => {
    setSearch('');
    setCompany('ALL');
    setActiveOnly(false);
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = () => {
    if (!peekTeam) return;
    setEditing(peekTeam);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  // Runs before the dialog's own close path, so `editing` still tells create
  // and update apart here.
  const handleFormSuccess = () => {
    toast.success(editing ? 'Team updated' : 'Team created');
  };

  const deleteTeam = useDeleteTeam();
  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    // Close synchronously so a second click can't queue a duplicate delete.
    setDeleting(null);
    deleteTeam.mutate(target.id, {
      onSuccess: () => {
        // Teams are server-paged and an out-of-range page 404s. The hook's
        // invalidation has already started a doomed refetch of this page by
        // the time this runs; stepping back switches the observer to the valid
        // page key in the same render batch, so that 404 lands on an
        // unobserved cache entry instead of the screen.
        if (rows.length === 1 && page > 1) setPage(page - 1);
        toast.success('Team deleted');
        setPeekOpen(false);
        setSelectedId(null);
      },
      onError: () => {
        toast.error('Delete failed');
      },
    });
  };

  const cancelRef = useRef<HTMLButtonElement>(null);
  const deleteConfirm = (
    <LedgerDialog
      open={deleting != null}
      onOpenChange={(next) => {
        if (!next) setDeleting(null);
      }}
      title="Delete team?"
      description={
        deleting ? `This permanently removes ${deleting.name}, including all of its departments.` : undefined
      }
      size="sm"
      preventClose={deleteTeam.isPending}
      initialFocus={cancelRef}
      footer={
        <>
          <button
            type="button"
            ref={cancelRef}
            onClick={() => setDeleting(null)}
            disabled={deleteTeam.isPending}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteTeam.isPending}
            className="cursor-pointer bg-sev-critical px-3 py-1.5 text-scale-1 font-semibold text-paper hover:opacity-90 disabled:cursor-default disabled:opacity-60"
          >
            Delete
          </button>
        </>
      }
    >
      {null}
    </LedgerDialog>
  );

  return (
    <PageWrapper
      title="Teams"
      subtitle={isPending ? undefined : `${count} team${count === 1 ? '' : 's'}`}
      noPaper
      maxWidth="xl"
      actions={
        <button
          type="button"
          onClick={openCreate}
          className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
        >
          + Add team
        </button>
      }
    >
      <OrgSubnav />

      <div className="hidden md:block">
        <TeamViews
          search={search}
          company={company}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onCompanyChange={changeCompany}
          onActiveOnlyChange={changeActiveOnly}
        />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <TeamViews
          search={search}
          company={company}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onCompanyChange={(next) => {
            changeCompany(next);
            setSheetOpen(false);
          }}
          onActiveOnlyChange={changeActiveOnly}
        />
      </LedgerSheet>

      <div className={cn('mt-1', peekOpen && peekTeam && isDesktop && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          <TeamsTable
            rows={rows}
            count={count}
            page={page}
            isPending={isPending}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onPageChange={setPage}
            onResetView={resetView}
          />
        </div>
        {peekOpen && peekTeam && isDesktop && (
          <TeamPeek team={peekTeam} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
        )}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekTeam != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="Team"
      >
        {peekTeam && (
          <>
            <TeamPeek team={peekTeam} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
            {/* Nested inside the sheet on purpose: sibling Base UI roots would
                both receive a single Escape and close together. */}
            {!isDesktop && deleteConfirm}
          </>
        )}
      </LedgerSheet>
      {isDesktop && deleteConfirm}

      <TeamFormDialog open={formOpen} onClose={closeForm} onSuccess={handleFormSuccess} team={editing} />
    </PageWrapper>
  );
};
